/**
 * Service for managing global and chart-specific filters.
 *
 * Provides:
 * - Global filter state management using signals
 * - Chart-specific filter state management
 * - Date range state with expression support
 * - Methods to add, remove, and clear filters
 */

import { Injectable, signal, computed } from '@angular/core';
import type { DateRangeExpression } from '../models/date-range-expression.model';

export interface Filter {
	key: string;
	value: any;
	operator?: 'equals' | 'contains' | 'greaterThan' | 'lessThan';
}

@Injectable({
	providedIn: 'root',
})
export class FilterService {
	private filtersSignal = signal<Filter[]>([]);
	private chartFiltersSignal = signal<Filter[]>([]);
	private dateRangeSignal = signal<{ startDate: string; endDate: string }>({
		startDate: '',
		endDate: '',
	});
	private dateRangeExpressionSignal = signal<DateRangeExpression | null>(null);

	readonly filters = computed(() => this.filtersSignal());
	readonly chartFilters = computed(() => this.chartFiltersSignal());
	readonly dateRange = computed(() => this.dateRangeSignal());
	/** Serializable date range expression for persistence/restoration */
	readonly dateRangeExpression = computed(() => this.dateRangeExpressionSignal());

	setDateRange(dateRange: { startDate: string; endDate: string }) {
		this.dateRangeSignal.set(dateRange);
	}

	/**
	 * Sets the date range along with its expression for persistence
	 */
	setDateRangeWithExpression(
		dateRange: { startDate: string; endDate: string },
		expression: DateRangeExpression
	) {
		this.dateRangeSignal.set(dateRange);
		this.dateRangeExpressionSignal.set(expression);
	}

	setDateRangeExpression(expression: DateRangeExpression | null) {
		this.dateRangeExpressionSignal.set(expression);
	}

	clearDateRangeExpression() {
		this.dateRangeExpressionSignal.set(null);
	}

	setFilters(filters: Filter[]) {
		this.filtersSignal.set(filters);
	}

	setChartFilters(filters: Filter[]) {
		this.chartFiltersSignal.set(filters);
	}

	addFilter(filter: Filter) {
		this.filtersSignal.update((filters) => [...filters, filter]);
	}

	removeFilter(key: string) {
		this.filtersSignal.update((filters) =>
			filters.filter((f) => f.key !== key)
		);
	}

	clearFilters() {
		this.filtersSignal.set([]);
	}

	clearChartFilters() {
		this.chartFiltersSignal.set([]);
	}
}
